import { createListItem, getListItems } from '../graph/lists';
import { newClientKey } from './clientKey';
import { LISTS } from './listNames';

/**
 * Ticket-style task thread: user comments and system events (completed,
 * reopened, reassigned, due-date change, file upload) share one list so the
 * task page renders a single chronological timeline. Append-only — there is
 * no edit/delete, the thread is the record. AuthorName carries the personal
 * name behind shared store accounts (same accountability pattern as task
 * completion). Flow 2c emails on new 'comment' rows, 'reassigned' events
 * (the new assignee) and 'reopened' events (the assignee, with the reason)
 * (docs/flows.md); other event rows are bookkeeping only.
 */

export type TaskEntryType = 'comment' | 'event';
export type TaskEventKind =
  | 'completed'
  | 'reopened'
  | 'reassigned'
  | 'due_date'
  | 'file';

export interface TaskThreadEntry {
  id: number;
  taskId: number;
  entryType: TaskEntryType;
  /** Set on event rows; '' on comments. */
  eventKind: TaskEventKind | '';
  body: string;
  authorUpn: string;
  authorName: string;
  /** Extra people the commenter chose to notify (comments only). */
  notifyUpns: string[];
  createdAt: string;
}

interface CommentFields {
  Title: string;
  TaskId: number;
  EntryType: string;
  EventKind: string;
  Body: string;
  AuthorUpn: string;
  AuthorName: string;
  NotifyUpns: string;
  TargetUpn: string;
  /** When it was posted (server-stamped since 2026-10-20); older rows fall back to Created. */
  PostedAt: string;
  /** Names this one create (src/data/clientKey.ts) — a resend is answered with the row it made. */
  ClientKey: string;
}

const EVENT_KINDS: readonly string[] = [
  'completed',
  'reopened',
  'reassigned',
  'due_date',
  'file',
];

/** Thread for one task (TaskId indexed), oldest first — sorted client-side. */
export async function getTaskThread(taskId: number): Promise<TaskThreadEntry[]> {
  const items = await getListItems<CommentFields>(LISTS.taskComments, {
    filter: `fields/TaskId eq ${taskId}`,
  });
  return items
    .map((i) => ({
      id: Number(i.id),
      taskId: i.fields.TaskId ?? taskId,
      entryType: (i.fields.EntryType === 'event' ? 'event' : 'comment') as TaskEntryType,
      eventKind: (EVENT_KINDS.includes(i.fields.EventKind ?? '')
        ? i.fields.EventKind
        : '') as TaskEventKind | '',
      body: i.fields.Body ?? '',
      authorUpn: i.fields.AuthorUpn ?? '',
      authorName: i.fields.AuthorName ?? '',
      notifyUpns: (i.fields.NotifyUpns ?? '')
        .split(';')
        .map((u) => u.trim())
        .filter(Boolean),
      // PostedAt can be back-dated (simulation history); SharePoint's Created can't.
      createdAt: i.fields.PostedAt || i.createdDateTime,
    }))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function addTaskComment(
  taskId: number,
  body: string,
  authorUpn: string,
  authorName: string,
  /** Extra recipients the commenter picked; Flow 2c CCs them verbatim. */
  notifyUpns: string[] = [],
  /** Keep the same key when retrying the same comment (SubmissionKey); default = a fresh one. */
  clientKey: string = newClientKey('cmt'),
): Promise<void> {
  await createListItem<CommentFields>(LISTS.taskComments, {
    Title: 'comment',
    TaskId: taskId,
    EntryType: 'comment',
    EventKind: '',
    Body: body,
    AuthorUpn: authorUpn,
    AuthorName: authorName,
    NotifyUpns: notifyUpns.join(';'),
    // The server stamps its own time; this only matters on the direct (rollback) path.
    PostedAt: new Date().toISOString(),
    ClientKey: clientKey,
  });
}

/**
 * System event row. Best-effort by convention at call sites: the state
 * change it describes must never be rolled back because the timeline write
 * failed.
 */
export async function addTaskEvent(
  taskId: number,
  kind: TaskEventKind,
  body: string,
  authorUpn: string,
  authorName: string,
  /**
   * The person the event is ABOUT (reassigned events: the new assignee).
   * Flow 2c emails this instead of the live Tasks row — the live read
   * loses a race when a task is reassigned twice in one poll window.
   */
  targetUpn = '',
  /** As addTaskComment's: a fresh key unless the caller retries the same event. */
  clientKey: string = newClientKey('ev'),
): Promise<void> {
  await createListItem<CommentFields>(LISTS.taskComments, {
    Title: kind,
    TaskId: taskId,
    EntryType: 'event',
    EventKind: kind,
    Body: body,
    AuthorUpn: authorUpn,
    AuthorName: authorName,
    TargetUpn: targetUpn,
    PostedAt: new Date().toISOString(),
    ClientKey: clientKey,
  });
}
